import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useCardWidth } from '@/hooks/use-card-width';
import { CARD_WIDTH_THRESHOLDS } from '@/lib/metric-view/responsive-thresholds';
import { buildSonarCloudUrl } from '@/lib/sonarcloud-url';
import { formatRelativeTime } from '@/lib/format-relative-time';

interface RepoQualityRowProps {
  repositoryId: string;
  repositoryName: string;
  projectKey: string;
  organization: string | null;
  lastAnalysedAt: string | null;
  /** Right-aligned primary metric cell (badge, % string, grade chip, …). */
  valueSlot: ReactNode;
}

/**
 * One row in any distribution-column panel. Layout adapts to the row's own
 * rendered width (`useCardWidth`): below `rowIconDrop` (280px, e.g. iPhone SE
 * portrait) the SonarCloud icon-button is dropped; the whole-row tap target
 * still navigates to the repo Code Quality tab.
 */
export function RepoQualityRow({
  repositoryId,
  repositoryName,
  projectKey,
  organization,
  lastAnalysedAt,
  valueSlot,
}: Readonly<RepoQualityRowProps>) {
  const { ref, width } = useCardWidth<HTMLLIElement>();
  const showSonarIcon = width === 0 || width >= CARD_WIDTH_THRESHOLDS.codeQuality.rowIconDrop;
  const sonarUrl = buildSonarCloudUrl(organization, projectKey);

  return (
    <li
      ref={ref}
      className="group flex items-center gap-2 rounded px-1 py-1 hover:bg-muted/40"
      data-testid="repo-quality-row"
    >
      <Link
        to={`/repositories/${repositoryId}?tab=code-quality`}
        className="flex flex-1 items-center gap-2 text-left text-xs text-foreground hover:text-primary"
        title={repositoryName}
      >
        <span className="flex-1 truncate font-medium">{repositoryName}</span>
        <span className="flex-shrink-0">{valueSlot}</span>
        <span className="flex-shrink-0 text-[10px] text-muted-foreground" title={lastAnalysedAt ?? ''}>
          {lastAnalysedAt ? formatRelativeTime(lastAnalysedAt) : '—'}
        </span>
      </Link>
      {showSonarIcon && (
        <a
          href={sonarUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${repositoryName} in SonarCloud`}
          className="flex-shrink-0 text-muted-foreground hover:text-foreground"
          data-testid="repo-quality-row-sonar-icon"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
            aria-hidden="true"
          >
            <path d="M14 3h7v7" />
            <path d="M21 3l-9 9" />
            <path d="M19 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
          </svg>
        </a>
      )}
    </li>
  );
}
